import Container from "components/core/Container";
import Heading from "components/elements/Heading";
import PreHeading from "components/elements/PreHeading";
import React from "react";
import { withAppLayout } from "components/core/withAppLayout";
import nextI18NextConfig from "./../../../next-i18next.config";
import { animated, useSpring } from 'react-spring';
import { getGlossaryEntries, getGlossaryEntriesCount, getGlossaryPage } from "queries/Glossary";
import Link from "components/base/Link";
import sortAlphabetically from "components/glossary/sortAlphabetically";
import { GetStaticPropsContext } from "next";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import { request } from "components/hooks/useQuery"; // Import the necessary modules

async function collectAllGlossaryEntries(context: GetStaticPropsContext) {
    const { _allGlossaryEntriesMeta: { count } } = await request({
        query: getGlossaryEntriesCount(),
    });

    let collected = [];

    let numberOfEntriesCollected = 0;

    while (numberOfEntriesCollected < count) {
        const { allGlossaryEntries } = await request({
            query: getGlossaryEntries(context.locale, numberOfEntriesCollected),
            locale: context.locale
        });

        numberOfEntriesCollected += allGlossaryEntries.length;

        collected.push(...allGlossaryEntries);
    }

    return collected;
}

export async function getStaticProps(context: GetStaticPropsContext) {
    const entries = await collectAllGlossaryEntries(context);

    const { glossaryPage, header, footer } = await request({
        query: getGlossaryPage(context.locale),
        withAppData: true,
        locale: context.locale
    });

    if (entries.length === 0) {
        return {
            notFound: true,
        };
    }

    return {
        props: {
            data: {
                allGlossaryEntries: entries
            },
            seo: glossaryPage?.seo,
            appData: {
                ...header,
                ...footer
            },
            ...(await serverSideTranslations(context.locale, ['common'], nextI18NextConfig)),
        },
    };
}

function Index(props: any) {
    const { allGlossaryEntries } = props;
    const sortedEntries = sortAlphabetically(allGlossaryEntries);
    const imageAppear = useSpring({
        from: { opacity: .5, marginTop: "10rem" },
        to: { opacity: 1, marginTop: "0rem" },
        delay: 50
    });

    return (
        <Container>
            <div className={"w-full h-full mt-8"}>
                <PreHeading
                    size={"medium"}
                    className={"tracking-widest mb-3 opacity-50"}
                >
                    Piggy
                </PreHeading>
                <Heading renderAs={"h1"}>
                    Glossary
                </Heading>
            </div>
            <div className={"grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 md:gap-x-8 gap-y-20 pt-10 lg:mt-0 mt-20"}>
                {sortedEntries.map((groupedEntry: any) => {
                    return (
                        <div key={groupedEntry.letter}>
                            <div className={'text-3xl font-semibold mb-2'}>
                                {groupedEntry.letter}
                            </div>
                            {groupedEntry.items.map((glossaryEntry: any) => {
                                return (
                                    <animated.div
                                        style={imageAppear}
                                        key={glossaryEntry.id}
                                    >
                                        <Link to={`/glossary/${glossaryEntry.slug}`}>
                                            <div>
                                                {glossaryEntry.title}
                                            </div>
                                        </Link>
                                    </animated.div>
                                );
                            })}
                        </div>
                    );
                })}
            </div>
        </Container>
    );
}

export default withAppLayout(Index);
